import React, { useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { IconArrowLeft } from '@tabler/icons-react';

import VirtualAccountResult from '@components/Result/VirtualAccount';
import QrisResult from '@components/Result/Qris';
import RedirectResult from '@components/Result/Redirect';
// import CardResult from '@components/Result/Card';

import { SettingsProps, DataDetailCheckout, PaymentType } from '@pages/types';
import { useCheckoutStep } from '@pages/hooks/useCheckoutStep';
import { useCheckoutStore } from '@stores/useCheckoutStore';

const PAYMENT_TYPES = {
  VA: 'va',
  EWALLET: 'ewallet',
  CARDS: 'cards',
  DIRECT_DEBIT: 'direct_debit',
} as const;

const MESSAGES = {
  FRAUD_WARNING: 'Protect Yourself Against Fraud',
  FRAUD_DESCRIPTION:
    "Ensure the merchant's name, Payment amount and details are correct Always check before proceeding with payment",
  CHECK_STATUS: 'CHECK STATUS',
  OPEN_EWALLET: 'OPEN YOUR E-WALLET',
  REDIRECT_CARD: 'REDIRECT TO URL CARD',
  DATA_UNAVAILABLE: 'Data not available. Please try again.',
  REDIRECT_UNAVAILABLE: 'Redirect URL tidak tersedia',
} as const;

const BackButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    onClick={onClick}
    className="flex w-max items-center gap-2 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-gray-700 transition-colors hover:text-gray-900"
  >
    <IconArrowLeft className="h-5 w-5" />
    <span className="font-medium">Back to Payment Methods</span>
  </button>
);

interface PaymentResultProps {
  data: DataDetailCheckout | null;
  settings: SettingsProps;
  onRefreshData: () => void;
}

const PaymentResult: React.FC<PaymentResultProps> = ({
  data,
  settings,
  onRefreshData,
}) => {
  const { goToMethod } = useCheckoutStep();
  const { selectedPaymentKey } = useCheckoutStore();

  const resolvedPaymentType =
    ((selectedPaymentKey || data?.payment?.string || '') as string) || '';
  const paymentType: PaymentType | string = resolvedPaymentType.includes('va')
    ? 'va'
    : resolvedPaymentType;

  const dataPaymentCategory = (data?.payment?.type || '').toLowerCase();
  const checkoutState = data?.checkout_state || '';

  const checkPayment = useCallback(() => {
    window.location.reload();
  }, []);

  const handleRedirect = useCallback((redirectUrl?: string) => {
    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else {
      toast.error(MESSAGES.REDIRECT_UNAVAILABLE);
    }
  }, []);

  const renderContent = useMemo(() => {
    if (!data?.payment) {
      return (
        <div className="flex h-[300px] items-center justify-center rounded-md bg-[#F5F5F5] p-6 text-gray-500 shadow-md">
          {MESSAGES.DATA_UNAVAILABLE}
        </div>
      );
    }

    const currentType = paymentType as PaymentType;

    const isProgressState = checkoutState === 'PAYMENT_IN_PROGRESS';
    const isRedirectState = checkoutState === 'PAYMENT_REDIRECT_REQUIRED';

    if (isProgressState) {
      if (
        currentType === PAYMENT_TYPES.VA ||
        dataPaymentCategory.includes('va')
      ) {
        return (
          <VirtualAccountResult
            data={data}
            settings={settings}
            onRefreshData={onRefreshData}
          />
        );
      }

      return <QrisResult data={data} settings={settings} />;
    }

    if (isRedirectState) {
      return (
        <RedirectResult
          data={data}
          settings={settings}
          onRefreshData={onRefreshData}
        />
      );
    }

    if (
      currentType === PAYMENT_TYPES.VA ||
      dataPaymentCategory.includes('va')
    ) {
      return (
        <VirtualAccountResult
          data={data}
          settings={settings}
          onRefreshData={onRefreshData}
        />
      );
    }

    const redirectTypes: PaymentType[] = [
      PAYMENT_TYPES.CARDS,
      PAYMENT_TYPES.EWALLET,
      PAYMENT_TYPES.DIRECT_DEBIT,
    ];

    if (
      redirectTypes.includes(currentType) ||
      redirectTypes.includes(dataPaymentCategory as PaymentType)
    ) {
      return (
        <RedirectResult
          data={data}
          settings={settings}
          onRefreshData={onRefreshData}
        />
      );
    }

    return <QrisResult data={data} settings={settings} />;
  }, [
    data,
    paymentType,
    checkoutState,
    settings,
    onRefreshData,
    handleRedirect,
    checkPayment,
  ]);

  return (
    <div className="mb-5 flex flex-col gap-4 px-3 pt-8 md:mb-0 md:pb-4 lg:px-0">
      <BackButton onClick={goToMethod} />
      {renderContent}
      <div className="fixed bottom-1 left-0 right-0 z-20 bg-white px-4 py-3 md:relative md:px-0">
        <div className="flex flex-row items-center justify-between gap-4 lg:gap-4">
          <button
            onClick={onRefreshData}
            className="w-full rounded-lg px-4 py-3 font-semibold text-white transition-opacity hover:opacity-90"
            style={{
              backgroundColor: settings.button_color
                ? settings.button_color
                : '#5D87FF',
              color: settings.button_text_color
                ? settings.button_text_color
                : '#FFFFFF',
            }}
          >
            {MESSAGES.CHECK_STATUS}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentResult;
